import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { ShoppingBag, Search, Phone, PhoneCall, PackageX, RotateCcw, Trash2, MapPin, Clock, Eye, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import ConfirmSheet from '@/components/ConfirmSheet';

const STATUS_TABS = [
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
  { value: 'all', label: 'All' },
];

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function AdminRequirements() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('open');
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [posts, setPosts] = useState([]);
  const [counts, setCounts] = useState({ open: 0, closed: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null); // { action, post }
  const [busy, setBusy] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/requirements', {
        params: {
          status,
          category: category === 'all' ? undefined : category,
          search: search || undefined,
        },
      });
      setPosts(data.requests || []);
      setCounts(data.counts || { open: 0, closed: 0, total: 0 });
    } catch {
      toast.error('Failed to load requirements');
    } finally {
      setLoading(false);
    }
  }, [api, status, category, search]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const runAction = async () => {
    if (!pending) return;
    const { action, post } = pending;
    setBusy(true);
    try {
      if (action === 'close') {
        await api.put(`/requirements/${post.id}`, { is_open: false });
        toast.success('Requirement closed');
      } else if (action === 'reopen') {
        await api.put(`/requirements/${post.id}`, { is_open: true });
        toast.success('Requirement reopened');
      } else if (action === 'delete') {
        await api.delete(`/requirements/${post.id}`);
        toast.success('Requirement deleted');
      }
      setPending(null);
      await fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const call = (phone) => {
    if (!phone) { toast.error('No phone on file'); return; }
    window.location.href = `tel:${phone}`;
  };

  return (
    <div className="space-y-4" data-testid="admin-requirements-section">
      {/* Status tabs */}
      <Tabs value={status} onValueChange={(v) => setStatus(v)}>
        <TabsList
          data-testid="admin-req-status-tabs"
          className="grid grid-cols-3 bg-[#EFF2F7] p-1 rounded-full h-auto"
        >
          {STATUS_TABS.map((t) => (
            <TabsTrigger
              key={t.value}
              value={t.value}
              data-testid={`admin-req-tab-${t.value}`}
              className="rounded-full text-[12px] font-semibold text-[#5B6675] data-[state=active]:bg-[#F6F8FB] data-[state=active]:text-[#0B1620] data-[state=active]:shadow-[0_2px_8px_-2px_rgba(11,22,32,0.08)]"
            >
              {t.label}
              <span className="ml-1.5 text-[10px] opacity-70 tabular-nums">
                {t.value === 'open' ? counts.open : t.value === 'closed' ? counts.closed : counts.total}
              </span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B6675]" />
        <Input
          data-testid="admin-req-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search title, buyer, location…"
          className="pl-11 h-11 bg-[#F1F5FA] border-[#E4E8EF] rounded-full text-[#0B1620] focus:ring-1 focus:ring-[#0B4F3F]/60 focus:border-[#0B4F3F]/60"
        />
      </div>

      {/* Category pills */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 pb-1">
        {['all','vegetables','fruits','grains','pulses','spices','dairy','oilseeds','other'].map((c) => (
          <button
            key={c}
            data-testid={`admin-req-cat-${c}`}
            onClick={() => setCategory(c)}
            className={`px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap border transition-colors ${
              category === c
                ? 'bg-[#0B4F3F] text-[#F6F8FB] border-[#0B4F3F]'
                : 'bg-[#F6F8FB] text-[#5B6675] border-[#E4E8EF] hover:text-[#0B1620]'
            }`}
          >
            {c === 'all' ? 'All categories' : c[0].toUpperCase() + c.slice(1)}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="space-y-3">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-32 rounded-2xl bg-[#EFF2F7] animate-pulse" />
          ))
        ) : posts.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-[#F6F8FB] border border-[#E4E8EF] flex items-center justify-center mb-3">
              <ShoppingBag className="w-6 h-6 text-[#0B4F3F]/60" />
            </div>
            <p className="text-[#0B1620] text-sm font-semibold">No requirements found</p>
            <p className="text-xs text-[#5B6675] mt-1">Try a different filter or search.</p>
          </div>
        ) : (
          posts.map((post) => (
            <div
              key={post.id}
              data-testid={`admin-req-${post.id}`}
              className="bg-[#F6F8FB] rounded-2xl border border-[#E4E8EF] p-4 space-y-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                      post.is_open ? 'bg-[#15803D]/10 text-[#15803D]' : 'bg-[#5B6675]/10 text-[#5B6675]'
                    }`}>
                      {post.is_open ? 'Open' : 'Closed'}
                    </span>
                    <span className="text-[10px] text-[#5B6675] flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {formatDate(post.created_at)}
                    </span>
                    <span className="text-[10px] text-[#5B6675] flex items-center gap-1">
                      <Eye className="w-3 h-3" /> {post.views || 0}
                    </span>
                    {post.callback_count > 0 && (
                      <span
                        data-testid={`admin-req-cbcount-${post.id}`}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#0B4F3F]/10 text-[#0B4F3F]"
                      >
                        <PhoneCall className="w-2.5 h-2.5" /> {post.callback_count} callback{post.callback_count > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-semibold text-[#0B1620] line-clamp-1">{post.title}</p>
                  <p className="text-[11px] text-[#5B6675] mt-0.5 uppercase tracking-wider">{post.category}</p>
                </div>
                <button
                  type="button"
                  data-testid={`admin-req-view-${post.id}`}
                  onClick={() => navigate(`/requirement/${post.id}`)}
                  aria-label="Open post"
                  className="w-8 h-8 rounded-full bg-[#EFF2F7] hover:bg-[#E4E8EF] flex items-center justify-center flex-shrink-0"
                >
                  <ExternalLink className="w-3.5 h-3.5 text-[#0B1620]" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2 text-[11px]">
                <div className="bg-[#EFF2F7] rounded-xl px-3 py-2">
                  <p className="text-[9px] uppercase text-[#5B6675]">Offer</p>
                  <p className="text-[12px] font-bold text-[#0B4F3F]">₹{post.price_per_unit}<span className="font-normal text-[#5B6675]">/{post.unit}</span></p>
                </div>
                <div className="bg-[#EFF2F7] rounded-xl px-3 py-2">
                  <p className="text-[9px] uppercase text-[#5B6675]">Quantity</p>
                  <p className="text-[12px] font-bold text-[#0B1620]">{post.quantity} {post.unit}</p>
                </div>
              </div>

              {post.description && (
                <p className="text-[12px] text-[#5B6675] line-clamp-2 leading-relaxed">{post.description}</p>
              )}

              {/* Buyer identity + PII (admin gets full contact) */}
              <div className="bg-[#EFF2F7] rounded-xl px-3 py-2.5 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[12px] font-semibold text-[#0B1620] truncate">
                    {post.user_name || 'Buyer'}
                    <span className="ml-1.5 text-[10px] font-normal text-[#5B6675] capitalize">· {post.user_role || 'vendor'}</span>
                  </p>
                  {post.user_phone && (
                    <button
                      type="button"
                      data-testid={`admin-req-call-${post.id}`}
                      onClick={() => call(post.user_phone)}
                      className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[11px] font-semibold text-[#0B4F3F] bg-[#F6F8FB] border border-[#0B4F3F]/25 hover:bg-[#0B4F3F]/8"
                    >
                      <Phone className="w-3 h-3" /> Call
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-3 text-[10.5px] text-[#5B6675]">
                  {post.user_phone && <span className="tabular-nums">{post.user_phone}</span>}
                  {post.location && (
                    <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {post.location}</span>
                  )}
                </div>
              </div>

              {/* Admin actions */}
              <div className="flex flex-wrap gap-2 pt-1 border-t border-[#E4E8EF]/60">
                {post.is_open ? (
                  <button
                    type="button"
                    data-testid={`admin-req-close-${post.id}`}
                    onClick={() => setPending({ action: 'close', post })}
                    className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-[11px] font-semibold text-[#0B4F3F] bg-[#0B4F3F]/8 hover:bg-[#0B4F3F]/12 border border-[#0B4F3F]/20"
                  >
                    <PackageX className="w-3 h-3" /> Close
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid={`admin-req-reopen-${post.id}`}
                    onClick={() => setPending({ action: 'reopen', post })}
                    className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-[11px] font-semibold text-[#15803D] bg-[#15803D]/8 hover:bg-[#15803D]/12 border border-[#15803D]/20"
                  >
                    <RotateCcw className="w-3 h-3" /> Reopen
                  </button>
                )}
                <button
                  type="button"
                  data-testid={`admin-req-delete-${post.id}`}
                  onClick={() => setPending({ action: 'delete', post })}
                  className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-[11px] font-semibold text-[#B91C1C] bg-[#F6F8FB] hover:bg-[#B91C1C]/8 border border-[#B91C1C]/20 ml-auto"
                >
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <ConfirmSheet
        open={!!pending}
        onOpenChange={(o) => { if (!o) setPending(null); }}
        title={
          pending?.action === 'delete'
            ? 'Delete this requirement?'
            : pending?.action === 'close'
              ? 'Close this requirement?'
              : 'Reopen this requirement?'
        }
        description={
          pending?.action === 'delete'
            ? `"${pending?.post?.title}" will be permanently removed. This cannot be undone.`
            : pending?.action === 'close'
              ? `Farmers won't be able to respond to "${pending?.post?.title}" anymore.`
              : `"${pending?.post?.title}" will be visible in the requirements feed again.`
        }
        confirmLabel={pending?.action === 'delete' ? 'Delete' : pending?.action === 'close' ? 'Close' : 'Reopen'}
        cancelLabel="Cancel"
        destructive={pending?.action === 'delete' || pending?.action === 'close'}
        icon={
          pending?.action === 'delete'
            ? <Trash2 className="w-6 h-6" strokeWidth={2.2} />
            : pending?.action === 'close'
              ? <PackageX className="w-6 h-6" strokeWidth={2.2} />
              : <RotateCcw className="w-6 h-6" strokeWidth={2.2} />
        }
        onConfirm={runAction}
        loading={busy}
        testId="admin-req-confirm"
      />
    </div>
  );
}
